/**
 * @author jzetlen
 * Apr 22 2016
 * Decorates `Backbone.sync` with prefetching behavior; if a request was made
 * with `{ prefetch: true }` as an option, then it will single-use cache the
 * response and return that response to a subsequent call to the same URL with
 * the same method and headers.
 *
 * @param {Function} syncFunction - the `Backbone.sync` method.
 * @returns {Function} a decorated, prefetching function to replace `Backbone.sync`.
 */
import { wrap, result, noop } from 'underscore';
export default function decorateSyncWithPrefetch(syncFunction) {
	// Cache to store prefetched transactions
	const prefetchCache = {};


	function isPrefetchable(method) {
		return method === 'read' || method === 'GET';
	}

	/**
	* This function create a unique key for transaction to be prefetched
	 * @param {String} method
	 * @param {Object} model - transaction model
	 * @param {String} rul - request url for the transaction
	 * @param {String} header - HTTP headers
	 * @return {String} unique key for prefetched transaction
	 */
	function createPrefetchCacheKey(method, model, { url, headers }) {
		const finalUrl = url || result(model, 'url');
		return finalUrl + JSON.stringify(headers || {});
	}

	function getPrefetchedResponse(cacheKey) {
		if (prefetchCache.hasOwnProperty(cacheKey)) {
			return prefetchCache[cacheKey];
		}
	}

	function deletePrefetchedResponse(cacheKey) {
		delete prefetchCache[cacheKey];
	}

	function storePrefetchedResponse(cacheKey, res) {
		prefetchCache[cacheKey] = res;
	}

	/**
	 * Warp Backbone.syc with prefething beheavior
	 */
	return wrap(syncFunction, function prefetchSync(sync, ...args) {
		if (isPrefetchable(...args)) {
			const key = createPrefetchCacheKey(...args);
			const [, , options] = args;
			options.success = options.success || noop;
			const prefetchedResponse = getPrefetchedResponse(key, ...args);

			// Deal with three scenarios:
			// if a transanction has cached-record, return
			// if a transaction prefetch flag is true but w/o cached-record, do a syn call and store
			// else for other transanctions, just do a syn call
			if (prefetchedResponse) {
				deletePrefetchedResponse(key, ...args);
				return prefetchedResponse.then(options.success);
			} else if (options.prefetch) {
				delete options.prefetch;
				const responsePromise = sync.apply(this, args);
				storePrefetchedResponse(key, responsePromise);
				return responsePromise;
			}
		}
		return sync.apply(this, args);
	});
}



// WEBPACK FOOTER //
// ./lib/prefetch-manager.es6